import { FC, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Collapse,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  Add as AddIcon,
  Remove as RemoveIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import type { ComparisonResult, InterfaceChange, InterfaceAdded, PropertyChange } from '@/types/entities';

interface InterfaceComparisonProps {
  comparison: ComparisonResult;
}

type FilterType = 'all' | 'added' | 'removed' | 'modified';
type ChangeType = 'added' | 'removed' | 'modified';

interface InterfaceRow {
  type: ChangeType;
  data: InterfaceAdded | InterfaceChange;
}

export const InterfaceComparison: FC<InterfaceComparisonProps> = ({ comparison }) => {
  const [filter, setFilter] = useState<FilterType>('all');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const { interfaceChanges } = comparison;

  const toggleRow = (uniqueId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(uniqueId)) {
      newExpanded.delete(uniqueId);
    } else {
      newExpanded.add(uniqueId);
    }
    setExpandedRows(newExpanded);
  };

  const getFilteredInterfaces = (): InterfaceRow[] => {
    const { added, removed, modified } = interfaceChanges;

    const rows: InterfaceRow[] = [];

    if (filter === 'all' || filter === 'added') {
      rows.push(...added.map(iface => ({ type: 'added' as ChangeType, data: iface })));
    }
    if (filter === 'all' || filter === 'removed') {
      rows.push(...removed.map(iface => ({ type: 'removed' as ChangeType, data: iface })));
    }
    if (filter === 'all' || filter === 'modified') {
      rows.push(...modified.map(iface => ({ type: 'modified' as ChangeType, data: iface })));
    }

    // Sort by name
    rows.sort((a, b) => {
      const nameA = 'name' in a.data ? a.data.name : '';
      const nameB = 'name' in b.data ? b.data.name : '';
      return nameA.localeCompare(nameB);
    });

    return rows;
  };

  const InterfaceRowComponent: FC<{ row: InterfaceRow }> = ({ row }) => {
    const { type, data } = row;
    const isModified = type === 'modified';

    // Use the correct type based on row type
    const interfaceData = data as (InterfaceAdded | InterfaceChange);
    const uniqueId = interfaceData.uniqueId;
    const name = interfaceData.name;
    const isExpanded = expandedRows.has(uniqueId);

    return (
      <>
        <TableRow hover>
          <TableCell width={50}>
            {isModified && (
              <IconButton size="small" onClick={() => toggleRow(uniqueId)}>
                {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
              </IconButton>
            )}
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontFamily="monospace">
              {name}
            </Typography>
          </TableCell>
          <TableCell>
            {type === 'added' && <Chip icon={<AddIcon />} label="Added" color="success" size="small" />}
            {type === 'removed' && <Chip icon={<RemoveIcon />} label="Removed" color="error" size="small" />}
            {type === 'modified' && (
              <Chip
                icon={<EditIcon />}
                label={`${(interfaceData as InterfaceChange).changes?.length || 0} changes`}
                color="warning"
                size="small"
              />
            )}
          </TableCell>
        </TableRow>

        {/* Expanded Detail for Modified Interfaces */}
        {isModified && (
          <TableRow>
            <TableCell colSpan={3} sx={{ py: 0 }}>
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Property Changes:
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Property</strong></TableCell>
                        <TableCell><strong>Before (A)</strong></TableCell>
                        <TableCell><strong>After (B)</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(interfaceData as InterfaceChange).changes?.map((propChange: PropertyChange, idx: number) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {propChange.propertyName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                textDecoration: 'line-through',
                                color: 'error.main',
                              }}
                            >
                              {formatValue(propChange.beforeValue)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ color: 'success.main', fontWeight: 'bold' }}
                            >
                              {formatValue(propChange.afterValue)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) {
      return '-';
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return String(value);
  };

  const filteredInterfaces = getFilteredInterfaces();

  return (
    <Box>
      {/* Filter Buttons */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Interface Changes</Typography>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, newFilter) => newFilter && setFilter(newFilter)}
          size="small"
        >
          <ToggleButton value="all">
            All ({interfaceChanges.added.length + interfaceChanges.removed.length + interfaceChanges.modified.length})
          </ToggleButton>
          <ToggleButton value="added">
            Added ({interfaceChanges.added.length})
          </ToggleButton>
          <ToggleButton value="removed">
            Removed ({interfaceChanges.removed.length})
          </ToggleButton>
          <ToggleButton value="modified">
            Modified ({interfaceChanges.modified.length})
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Comparison Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50}></TableCell>
              <TableCell><strong>Interface</strong></TableCell>
              <TableCell><strong>Change</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInterfaces.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography color="text.secondary">No {filter !== 'all' ? filter : ''} interfaces</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredInterfaces.map((row) => (
                <InterfaceRowComponent key={('uniqueId' in row.data ? row.data.uniqueId : '')} row={row} />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Statistics */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Total Changes:</strong> {filteredInterfaces.length} interfaces
        </Typography>
        {filter === 'all' && (
          <>
            <Typography variant="body2" color="success.main">
              +{interfaceChanges.added.length}
            </Typography>
            <Typography variant="body2" color="error.main">
              -{interfaceChanges.removed.length}
            </Typography>
            <Typography variant="body2" color="warning.main">
              ~{interfaceChanges.modified.length}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};
